import * as pdfjs from 'pdfjs-dist';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './slide-viewer.css';
pdfjs.GlobalWorkerOptions.workerSrc = '/libs/pdf.worker.min.js';
const SlideViewer = ({ pdfUrl, width = '100%', height = 600 }) => {
    const canvasRef = useRef(null);
    const [pdf, setPdf] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const renderTaskRef = useRef(null);
    useEffect(() => {
        if (!pdfUrl)
            return;
        setLoading(true);
        setError(null);
        setCurrentPage(1);
        setPdf(null);
        const loadingTask = pdfjs.getDocument(pdfUrl);
        loadingTask.promise
            .then(doc => {
            setTotalPages(doc.numPages);
            setPdf(doc);
        })
            .catch(() => {
            setError('PDFの読み込みに失敗しました');
            setLoading(false);
        });
        return () => { loadingTask.destroy(); };
    }, [pdfUrl]);
    const renderPage = useCallback(async (doc, pageNum) => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
            renderTaskRef.current = null;
        }
        const page = await doc.getPage(pageNum);
        const containerWidth = canvas.parentElement?.clientWidth ?? 800;
        const base = page.getViewport({ scale: 1 });
        // レイアウト上の表示スケール（コンテナ幅にフィット）。
        const cssScale = containerWidth / base.width;
        // バッキングストアは devicePixelRatio 倍の解像度で描画する。
        // カードを拡大するたびに ResizeObserver 経由で「表示サイズ×DPR」の
        // 解像度で再描画されるので、拡大してもボケない。
        const dpr = window.devicePixelRatio || 1;
        // 拡大しすぎで canvas が巨大化しメモリを食うのを防ぐ上限。
        const MAX_CANVAS_W = 4096;
        let renderScale = cssScale * dpr;
        if (base.width * renderScale > MAX_CANVAS_W) {
            renderScale = MAX_CANVAS_W / base.width;
        }
        const cssViewport = page.getViewport({ scale: cssScale });
        const renderViewport = page.getViewport({ scale: renderScale });
        // 高解像度で描画し、CSS で表示サイズに縮める → くっきり表示。
        canvas.width = Math.floor(renderViewport.width);
        canvas.height = Math.floor(renderViewport.height);
        canvas.style.width = `${Math.floor(cssViewport.width)}px`;
        canvas.style.height = `${Math.floor(cssViewport.height)}px`;
        const renderTask = page.render({ canvas, viewport: renderViewport });
        renderTaskRef.current = renderTask;
        try {
            await renderTask.promise;
            setLoading(false);
        }
        catch (err) {
            if (err?.name !== 'RenderingCancelledException') {
                setError('ページのレンダリングに失敗しました');
                setLoading(false);
            }
        }
    }, []);
    useEffect(() => {
        if (pdf) {
            renderPage(pdf, currentPage);
        }
    }, [pdf, currentPage, renderPage]);
    // カード(コンテナ)がリサイズされたら、新しい表示サイズ×DPR の解像度で
    // 現在ページを描き直す。CSS で引き伸ばさないのでボケない。
    useEffect(() => {
        const wrapper = canvasRef.current?.parentElement;
        if (!wrapper || !pdf || typeof ResizeObserver === 'undefined') {
            return;
        }
        let raf = 0;
        let lastW = wrapper.clientWidth;
        const ro = new ResizeObserver(() => {
            const w = wrapper.clientWidth;
            if (Math.abs(w - lastW) < 2) { // サブピクセルのゆらぎは無視
                return;
            }
            lastW = w;
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => renderPage(pdf, currentPage));
        });
        ro.observe(wrapper);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [pdf, currentPage, renderPage]);
    const goToPrev = () => setCurrentPage(p => Math.max(1, p - 1));
    const goToNext = () => setCurrentPage(p => Math.min(totalPages, p + 1));
    return (React.createElement("div", { className: 'slide-viewer', style: { width } },
        React.createElement("div", { className: 'slide-viewer__canvas-wrapper', style: { height } },
            React.createElement("canvas", { className: 'slide-viewer__canvas', style: { visibility: loading || error ? 'hidden' : 'visible' }, ref: canvasRef }),
            loading && !error && React.createElement("div", { className: 'slide-viewer__overlay' }, "\u8AAD\u307F\u8FBC\u307F\u4E2D..."),
            error && React.createElement("div", { className: 'slide-viewer__overlay slide-viewer__overlay--error' }, error)),
        !loading && !error && (React.createElement("div", { className: 'slide-viewer__controls' },
            React.createElement("button", { className: 'slide-viewer__btn', disabled: currentPage <= 1, onClick: goToPrev }, "\u2039"),
            React.createElement("span", { className: 'slide-viewer__page-info' },
                currentPage,
                " / ",
                totalPages),
            React.createElement("button", { className: 'slide-viewer__btn', disabled: currentPage >= totalPages, onClick: goToNext }, "\u203A")))));
};
export default SlideViewer;
